/**
* Updates the password validation UI elements with the validation results
* @param {Object} validationResults - an object containing the validation results
* @param {boolean} validationResults.length - indicates if the password meets the minimum length requirement
* @param {boolean} validationResults.uppercase - indicates if the password contains at least one uppercase letter
* @param {boolean} validationResults.lowercase - indicates if the password contains at least one lowercase letter
* @param {boolean} validationResults.numberOrSpecialChar - indicates if the password contains at least one number or special character
* @param {HTMLElement} parentForm - the parent form element
*/
const updateClasses = (validationResults, parentForm) => {
    const lengthItem = parentForm.querySelector(".password-validation__minimum");
    const uppercaseItem = parentForm.querySelector(".password-validation__uppercase");
    const lowercaseItem = parentForm.querySelector(".password-validation__lowercase");
    const numberOrSpecialCharItem = parentForm.querySelector(".password-validation__specialchar");
    lengthItem.setAttribute("data-valid", validationResults.length);
    uppercaseItem.setAttribute("data-valid", validationResults.uppercase);
    lowercaseItem.setAttribute("data-valid", validationResults.lowercase);
    numberOrSpecialCharItem.setAttribute("data-valid", validationResults.numberOrSpecialChar);
};

/**
* Validates the password input for minimum length, uppercase, lowercase, and number/special character presence
* @param {HTMLInputElement} passwordInput - the input field for password
* @param {HTMLElement} parentForm - the parent form element
* @returns {Object} - an object containing validation results
*  - {boolean} length - indicates if the password meets the minimum length requirement
*  - {boolean} uppercase - indicates if the password contains at least one uppercase letter
*  - {boolean} lowercase - indicates if the password contains at least one lowercase letter
*  - {boolean} numberOrSpecialChar - indicates if the password contains at least one number or special character
*  - {boolean} isValid - indicates if the password is valid based on all validation criteria
*/
const validatePassword = (passwordInput, parentForm) => {
    const password = passwordInput.value;

    const minLength = 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumberOrSpecialChar = /[0-9$&+,:;=?@#|'<>.^*()%!\-_]/.test(password);

    const validationResults = {
        length: password.length >= minLength,
        uppercase: hasUppercase,
        lowercase: hasLowercase,
        numberOrSpecialChar: hasNumberOrSpecialChar
    };
    updateClasses(validationResults, parentForm);
};

/**
* Validates the password input on keyup event
* @param {HTMLElement} passwordInput - the password input field element
* @returns {void} This function validates the password input and updates the validation classes.
* @see validatePassword
* @see updateClasses
*/
module.exports = () => {
    const passwordInputs = document.querySelectorAll(".passwordField");

    if (passwordInputs) {
        passwordInputs.forEach((passwordInput) => {
            passwordInput.addEventListener("keyup", () => {
                const parentForm = passwordInput.closest("form");
                validatePassword(passwordInput, parentForm);
            });
        });
    }
};
